import { Component } from '@theme/component';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Mobile PLP toolbar wired to Horizon facets drawers.
 *
 * @extends {Component<never>}
 */
class KilwinsPlpToolbar extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(DrawerOpenEvent.eventName, this.#syncDrawerExpandedState, { signal });
    document.addEventListener(DrawerCloseEvent.eventName, this.#syncDrawerExpandedState, { signal });
    document.addEventListener(DrawerCloseEvent.eventName, this.#resetDrawerView, { signal });
    this.#syncDrawerExpandedState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  #syncDrawerExpandedState = () => {
    const drawer = document.getElementById('filters-drawer');
    const isOpen = drawer?.hasAttribute('open') ?? false;

    for (const button of this.querySelectorAll('.kilwins-plp-toolbar__button')) {
      button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  };

  #resetDrawerView = () => {
    const drawer = document.getElementById('filters-drawer');
    if (!drawer) return;

    delete drawer.dataset.drawerView;
    this.#setFiltersInert(drawer, false);
  };

  /**
   * @param {HTMLElement} drawer
   * @param {'filters' | 'sort'} view
   */
  #setDrawerView(drawer, view) {
    drawer.dataset.drawerView = view;
    this.#setFiltersInert(drawer, view === 'sort');
  }

  /**
   * @param {HTMLElement} drawer
   * @param {boolean} inert
   */
  #setFiltersInert(drawer, inert) {
    for (const element of drawer.querySelectorAll('.filter-remove-buttons, .facets__filters-wrapper')) {
      if (inert) {
        element.setAttribute('inert', '');
        element.setAttribute('aria-hidden', 'true');
      } else {
        element.removeAttribute('inert');
        element.removeAttribute('aria-hidden');
      }
    }
  }

  /**
   * @param {Event} event
   */
  openFilters(event) {
    const drawer = document.getElementById('filters-drawer');
    if (!drawer || !('open' in drawer) || typeof drawer.open !== 'function') return;

    this.#setDrawerView(drawer, 'filters');
    drawer.open(this.#getDrawerOpenOptions(event));
  }

  /**
   * @param {Event} event
   */
  openSort(event) {
    const drawer = document.getElementById('filters-drawer');
    if (!drawer || !('open' in drawer) || typeof drawer.open !== 'function') return;

    this.#setDrawerView(drawer, 'sort');
    drawer.open(this.#getDrawerOpenOptions(event));

    requestAnimationFrame(() => {
      if (drawer.dataset.drawerView !== 'sort' || !drawer.hasAttribute('open')) return;
      this.#focusSortControl(drawer);
    });
  }

  /**
   * @param {Event} event
   * @returns {{ event: Event, trigger?: HTMLElement }}
   */
  #getDrawerOpenOptions(event) {
    const trigger = event?.currentTarget;
    return {
      event,
      trigger: trigger instanceof HTMLElement ? trigger : undefined,
    };
  }

  /**
   * @param {HTMLElement} drawer
   */
  #focusSortControl(drawer) {
    const sortComponent = drawer.querySelector('sorting-filter-component');
    if (!sortComponent) return;

    const select = sortComponent.querySelector('select.sorting-filter__select:not([disabled])');
    if (select instanceof HTMLSelectElement && select.getClientRects().length > 0) {
      select.focus();
      select.scrollIntoView({ block: 'nearest' });
      return;
    }

    const sortDetails = sortComponent.querySelector('details[id^="Sorting-"]');
    if (!(sortDetails instanceof HTMLDetailsElement)) return;

    sortDetails.open = true;
    sortDetails.scrollIntoView({ block: 'nearest' });

    const selectedOption = sortDetails.querySelector('[role="option"][aria-selected="true"]');
    if (selectedOption instanceof HTMLElement) {
      selectedOption.focus();
      return;
    }

    const summary = sortDetails.querySelector('summary');
    if (summary instanceof HTMLElement) {
      summary.focus();
    }
  }
}

if (!customElements.get('kilwins-plp-toolbar')) {
  customElements.define('kilwins-plp-toolbar', KilwinsPlpToolbar);
}
